import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { User, FileText, Save, Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCandidateDraft } from "@/hooks/useCandidateDraft";

interface InterviewFormProps {
  candidateId: string;
  candidateName: string;
  interviewType: 'first' | 'second';
  interviewId: string;
  onCompleted: () => void;
  onCancel: () => void;
}

export function InterviewForm({ 
  candidateId, 
  candidateName, 
  interviewType, 
  interviewId,
  onCompleted, 
  onCancel 
}: InterviewFormProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  // Use secure database-backed draft instead of localStorage
  const { draft, saveDraft, deleteDraft, isSaving } = useCandidateDraft(`interview_eval_${interviewType}`, candidateId);
  
  const [evaluation, setEvaluation] = useState(() => {
    // Load from secure database draft
    if (draft) {
      return draft;
    }
    
    // Default values
    return {
      overall_impression: '3',
      communication_skills: '3',
      technical_knowledge: '3',
      experience_relevance: '3',
      professionalism: '3',
      problem_solving: '3',
      physical_fitness: '3',
      reliability_assessment: '3',
      strengths: '',
      weaknesses: '',
      recommendation: 'proceed',
      additional_notes: ''
    };
  });

  // Save to database whenever evaluation changes (debounced via hook)
  useEffect(() => {
    const hasData = Object.values(evaluation).some(v => v !== "" && v !== "proceed" && v !== "3");
    if (hasData) {
      saveDraft(evaluation);
    }
  }, [evaluation]);

  const evaluationCriteria = [
    { key: 'overall_impression', label: 'Συνολική Εντύπωση' },
    { key: 'communication_skills', label: 'Επικοινωνιακές Δεξιότητες' },
    { key: 'technical_knowledge', label: 'Τεχνική Γνώση' },
    { key: 'experience_relevance', label: 'Συναφής Εμπειρία' },
    { key: 'professionalism', label: 'Επαγγελματισμός' },
    { key: 'problem_solving', label: 'Επίλυση Προβλημάτων' },
    { key: 'physical_fitness', label: 'Φυσική Κατάσταση' },
    { key: 'reliability_assessment', label: 'Αξιοπιστία' }
  ];

  const scoreLabels = {
    '1': 'Πολύ Χαμηλή',
    '2': 'Χαμηλή', 
    '3': 'Μέτρια',
    '4': 'Καλή',
    '5': 'Εξαιρετική'
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Calculate average score
      const scores = evaluationCriteria.map(criteria => parseInt(evaluation[criteria.key as keyof typeof evaluation] as string));
      const averageScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;

      // Update interview with evaluation
      const { error: interviewError } = await supabase
        .from('interviews')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          evaluation_score: Math.round(averageScore),
          evaluation_notes: evaluation.additional_notes,
          recommendation: evaluation.recommendation
        })
        .eq('id', interviewId);

      if (interviewError) throw interviewError;

      // Create evaluation record
      const { error: evaluationError } = await supabase
        .from('candidate_evaluations')
        .insert({
          candidate_id: candidateId,
          interview_id: interviewId,
          evaluation_type: interviewType === 'first' ? 'first_interview' : 'second_interview',
          overall_impression: parseInt(evaluation.overall_impression),
          communication_skills: parseInt(evaluation.communication_skills),
          technical_knowledge: parseInt(evaluation.technical_knowledge),
          experience_relevance: parseInt(evaluation.experience_relevance),
          professionalism: parseInt(evaluation.professionalism),
          problem_solving: parseInt(evaluation.problem_solving),
          physical_fitness: parseInt(evaluation.physical_fitness),
          reliability_assessment: parseInt(evaluation.reliability_assessment),
          strengths: evaluation.strengths,
          weaknesses: evaluation.weaknesses,
          additional_notes: evaluation.additional_notes
        });

      if (evaluationError) throw evaluationError;

      // Update workflow stage
      const nextStage = interviewType === 'first' ? 'first_interview_evaluation' : 'tests_available';
      
      await supabase
        .from('hr_workflow_stages')
        .upsert({
          candidate_id: candidateId,
          stage_name: nextStage,
          status: 'pending',
          completed_at: new Date().toISOString(),
          notes: `${interviewType === 'first' ? 'Πρώτη' : 'Δεύτερη'} συνέντευξη ολοκληρώθηκε. Βαθμολογία: ${Math.round(averageScore)}/5. Σύσταση: ${evaluation.recommendation}`
        });

      // Update candidate status based on recommendation
      let newStatus;
      if (interviewType === 'first') {
        newStatus = evaluation.recommendation === 'reject' ? 'rejected' : 'first_interview_evaluation';
      } else {
        newStatus = evaluation.recommendation === 'reject' ? 'rejected' : 'tests_available';
      }
      
      await supabase
        .from('candidates')
        .update({ status: newStatus })
        .eq('id', candidateId);

      toast({
        title: "Επιτυχία",
        description: `Η αξιολόγηση της ${interviewType === 'first' ? 'πρώτης' : 'δεύτερης'} συνέντευξης καταχωρήθηκε επιτυχώς`,
      });

      // Clear saved data after successful submission
      await deleteDraft();
      
      onCompleted();
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="w-5 h-5" />
          Αξιολόγηση {interviewType === 'first' ? 'Πρώτης' : 'Δεύτερης'} Συνέντευξης
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Υποψήφιος: <span className="font-medium">{candidateName}</span>
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Evaluation Criteria */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Star className="w-5 h-5" />
              Κριτήρια Αξιολόγησης
            </h3>
            
            <div className="grid gap-6">
              {evaluationCriteria.map((criteria) => (
                <div key={criteria.key} className="space-y-3">
                  <Label className="text-base font-medium">{criteria.label}</Label>
                  <RadioGroup
                    value={evaluation[criteria.key as keyof typeof evaluation] as string}
                    onValueChange={(value) => setEvaluation({...evaluation, [criteria.key]: value})}
                    className="flex space-x-6"
                  >
                    {Object.entries(scoreLabels).map(([score, label]) => (
                      <div key={score} className="flex items-center space-x-2">
                        <RadioGroupItem value={score} id={`${criteria.key}-${score}`} />
                        <Label htmlFor={`${criteria.key}-${score}`} className="text-sm">
                          {score} - {label}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ))}
            </div>
          </div>

          {/* Qualitative Assessment */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Ποιοτική Αξιολόγηση
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="strengths">Δυνατά Σημεία</Label>
                <Textarea
                  id="strengths"
                  value={evaluation.strengths}
                  onChange={(e) => setEvaluation({...evaluation, strengths: e.target.value})}
                  placeholder="Περιγράψτε τα βασικά δυνατά σημεία του υποψηφίου..."
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="weaknesses">Αδυναμίες / Σημεία Βελτίωσης</Label>
                <Textarea
                  id="weaknesses"
                  value={evaluation.weaknesses}
                  onChange={(e) => setEvaluation({...evaluation, weaknesses: e.target.value})}
                  placeholder="Περιγράψτε τυχόν αδυναμίες ή σημεία που χρειάζονται βελτίωση..."
                  rows={4}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="additional_notes">Επιπρόσθετες Παρατηρήσεις</Label>
              <Textarea
                id="additional_notes"
                value={evaluation.additional_notes}
                onChange={(e) => setEvaluation({...evaluation, additional_notes: e.target.value})}
                placeholder="Οποιαδήποτε επιπρόσθετη πληροφορία που θεωρείτε σημαντική..."
                rows={3}
              />
            </div>
          </div>

          {/* Recommendation */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Σύσταση</h3>
            <RadioGroup
              value={evaluation.recommendation}
              onValueChange={(value) => setEvaluation({...evaluation, recommendation: value})}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="proceed" id="proceed" />
                <Label htmlFor="proceed" className="flex items-center gap-2">
                  <Badge className="bg-green-100 text-green-800">Συνέχεια</Badge>
                  {interviewType === 'first' ? 'Προχώρηση σε δεύτερη συνέντευξη' : 'Προχώρηση σε ψυχολογικά τεστ'}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="conditional" id="conditional" />
                <Label htmlFor="conditional" className="flex items-center gap-2">
                  <Badge className="bg-yellow-100 text-yellow-800">Υπό Όρους</Badge>
                  Συνέχεια με επιφυλάξεις
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="reject" id="reject" />
                <Label htmlFor="reject" className="flex items-center gap-2">
                  <Badge className="bg-red-100 text-red-800">Απόρριψη</Badge>
                  Δεν συνιστάται συνέχεια
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              Ακύρωση
            </Button>
            <Button type="submit" disabled={loading}>
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Αποθήκευση...' : 'Αποθήκευση Αξιολόγησης'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}